import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AppMenu } from './app.menu';
import { LayoutService } from '@/app/layout/service/layout.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppMenu, RouterModule],
  template: `
    <div
      class="layout-sidebar"
      (touchstart)="onSidebarTouchStart($event)"
      (touchend)="onSidebarTouchEnd($event)"
    >
      <!-- Drag handle for mobile bottom sheet -->
      <div class="layout-sidebar-handle">
        <div class="layout-sidebar-handle-bar"></div>
      </div>
      <app-menu></app-menu>
    </div>
  `
})
export class AppSidebar implements OnDestroy {
  layoutService = inject(LayoutService);
  router = inject(Router);
  el = inject(ElementRef);

  private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  // Swipe-to-close (swipe down for bottom sheet on mobile, swipe left for sidebar on desktop)
  private readonly MIN_SWIPE_DISTANCE = 60;
  private sidebarTouchStartX = 0;
  private sidebarTouchStartY = 0;

  onSidebarTouchStart(e: TouchEvent) {
    this.sidebarTouchStartX = e.touches[0].clientX;
    this.sidebarTouchStartY = e.touches[0].clientY;
  }

  onSidebarTouchEnd(e: TouchEvent) {
    if (!this.layoutService.layoutState().mobileMenuActive) return;

    const deltaX = Math.abs(e.changedTouches[0].clientX - this.sidebarTouchStartX);
    const deltaY = e.changedTouches[0].clientY - this.sidebarTouchStartY;

    // Swipe down to close bottom sheet (deltaY > 0 means down direction)
    if (deltaY > this.MIN_SWIPE_DISTANCE && deltaY > deltaX) {
      this.layoutService.layoutState.update((val) => ({
        ...val,
        mobileMenuActive: false,
        menuHoverActive: false
      }));
    }
  }

  constructor() {
    effect(() => {
      const state = this.layoutService.layoutState();

      if (this.layoutService.isDesktop()) {
        if (state.overlayMenuActive) {
          this.bindOutsideClickListener();
        } else {
          this.unbindOutsideClickListener();
        }
      } else {
        if (state.mobileMenuActive) {
          this.bindOutsideClickListener();
        } else {
          this.unbindOutsideClickListener();
        }
      }
    });

    this.router.events
    .pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed()
    )
    .subscribe((event) => {
      const navEvent = event as NavigationEnd;
      this.onRouteChange(navEvent.urlAfterRedirects);
    });

    this.onRouteChange(this.router.url);
  }

  ngOnDestroy() {
    this.unbindOutsideClickListener();
  }

  private onRouteChange(path: string) {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      activePath: path,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      mobileMenuActive: false,
      menuHoverActive: false
    }));
  }

  private bindOutsideClickListener() {
    if (!this.outsideClickListener) {
      this.outsideClickListener = (event: MouseEvent) => {
        if (this.isOutsideClicked(event)) {
          this.layoutService.layoutState.update((val) => ({
            ...val,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            mobileMenuActive: false,
            menuHoverActive: false
          }));
        }
      };

      document.addEventListener('click', this.outsideClickListener);
    }
  }

  private unbindOutsideClickListener() {
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
      this.outsideClickListener = null;
    }
  }

  private isOutsideClicked(event: MouseEvent): boolean {
    const topbarButtonEl = document.querySelector('.layout-menu-button');
    const fabButtonEl = document.querySelector('.layout-mobile-fab');
    const sidebarEl = this.el.nativeElement;

    return !(
      sidebarEl?.isSameNode(event.target as Node) ||
      sidebarEl?.contains(event.target as Node) ||
      topbarButtonEl?.isSameNode(event.target as Node) ||
      topbarButtonEl?.contains(event.target as Node) ||
      fabButtonEl?.isSameNode(event.target as Node) ||
      fabButtonEl?.contains(event.target as Node)
    );
  }
}
